import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api } from "@/lib/api";
import { normalizeText } from "@/lib/text";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Check, PaperPlaneTilt, MagnifyingGlass, X, Broom, Trash, WarningCircle, ArrowClockwise, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAccessGate } from "@/hooks/useAccessGate";

const INITIAL_FILTERS = { q: "", template_id: "all", status: "all", date_from: "", date_to: "" };

export default function Assignments() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [copiedId, setCopiedId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // assignment | null
  const [deletingAsg, setDeletingAsg] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { gateProps } = useAccessGate();

  const load = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/assignments");
      setItems(data);
    } catch (err) {
      if (!silent) toast.error("Impossible de rafraîchir la liste");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };
  useEffect(() => {
    load({ silent: true });
    (async () => {
      const { data } = await api.get("/templates");
      setTemplates(data);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = normalizeText(filters.q.trim());
    const from = filters.date_from ? new Date(filters.date_from + "T00:00:00") : null;
    const to = filters.date_to ? new Date(filters.date_to + "T23:59:59") : null;
    return items.filter((a) => {
      if (filters.template_id !== "all" && a.template_id !== filters.template_id) return false;
      // Hide superseded (older duplicate sends replaced when patient answered a sibling link)
      if (a.status === "superseded") return false;
      const draftCount = typeof a.draft_answered_count === "number"
        ? a.draft_answered_count
        : (a.draft_answers ? Object.keys(a.draft_answers).length : 0);
      const inProgress = a.status === "sent"
        && !!a.draft_saved_at
        && draftCount > 0;
      if (filters.status === "in_progress") {
        if (!inProgress) return false;
      } else if (filters.status !== "all" && a.status !== filters.status) return false;
      if (filters.hide_deleted && a.template_is_deleted && a.status !== "completed") return false;
      if (from || to) {
        const at = a.sent_at ? new Date(a.sent_at) : null;
        if (!at) return false;
        if (from && at < from) return false;
        if (to && at > to) return false;
      }
      if (q) {
        const hay = normalizeText(`${a.patient_name || ""} ${a.patient_email || ""} ${a.template_title || ""}`);
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters]);

  // Count in-progress assignments so we can advertise the filter option in the
  // dropdown (helps the practitioner discover the completion-rate lens).
  const inProgressCount = useMemo(
    () => items.filter((a) => {
      const c = typeof a.draft_answered_count === "number"
        ? a.draft_answered_count
        : (a.draft_answers ? Object.keys(a.draft_answers).length : 0);
      return a.status === "sent" && !!a.draft_saved_at && c > 0;
    }).length,
    [items],
  );

  // Pending items in current view — only these are bulk-resendable
  const eligible = useMemo(
    () => filtered.filter((a) => a.status === "sent" && a.patient_email && !a.template_is_deleted),
    [filtered],
  );
  const eligibleIds = useMemo(() => eligible.map((a) => a.assignment_id), [eligible]);
  const selectedInView = useMemo(
    () => eligibleIds.filter((id) => selected.has(id)),
    [eligibleIds, selected],
  );
  const allSelected = eligibleIds.length > 0 && selectedInView.length === eligibleIds.length;
  const someSelected = selectedInView.length > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllInView = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        eligibleIds.forEach((id) => next.delete(id));
      } else {
        eligibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const hasFilters =
    filters.q ||
    filters.template_id !== "all" ||
    filters.status !== "all" ||
    filters.date_from ||
    filters.date_to;
  const reset = () => setFilters(INITIAL_FILTERS);

  const copyLink = async (asg) => {
    const link = `${window.location.origin}/q/${asg.token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(asg.assignment_id);
      toast.success("Lien copié");
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Copie impossible — copiez le lien manuellement");
    }
  };

  const resend = async (asg) => {
    setResendingId(asg.assignment_id);
    try {
      const { data } = await api.post(`/assignments/${asg.assignment_id}/resend`);
      toast.success(`Email renvoyé à ${data.sent_to}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Renvoi impossible");
    } finally {
      setResendingId(null);
    }
  };

  const bulkResend = async () => {
    const ids = selectedInView;
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/assignments/bulk-resend", { assignment_ids: ids });
      const failed = data.results.filter((r) => !r.ok);
      if (failed.length === 0) {
        toast.success(`${data.ok_count} rappel${data.ok_count > 1 ? "s" : ""} envoyé${data.ok_count > 1 ? "s" : ""}`);
      } else if (data.ok_count === 0) {
        toast.error(`Aucun rappel envoyé — ${failed[0].error}`);
      } else {
        toast.warning(`${data.ok_count} / ${data.total} rappels envoyés. ${failed.length} échec${failed.length > 1 ? "s" : ""}.`);
      }
      clearSelection();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Rappel groupé impossible");
    } finally {
      setBulkBusy(false);
    }
  };

  // Orphaned = pending + template deleted, across the *whole* list (not just filtered view)
  const orphanCount = useMemo(
    () => items.filter((a) => a.status === "sent" && a.template_is_deleted).length,
    [items],
  );

  const cleanupOrphans = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/assignments/archive-orphaned");
      toast.success(data.archived
        ? `${data.archived} envoi${data.archived > 1 ? "s" : ""} orphelin${data.archived > 1 ? "s" : ""} archivé${data.archived > 1 ? "s" : ""}`
        : "Aucun envoi orphelin à archiver");
      setConfirmCleanup(false);
      clearSelection();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Archivage impossible");
    } finally { setCleanupBusy(false); }
  };

  return (
    <Protected>
      <AppShell
        title="Envois"
        actions={
          <Button
            variant="outline"
            onClick={() => load()}
            disabled={refreshing}
            className="rounded-[10px] font-semibold group disabled:opacity-50 disabled:cursor-wait"
            data-testid="refresh-assignments-btn"
            title="Rafraîchir"
          >
            <ArrowClockwise size={14} weight="bold" className={`mr-1.5 ${refreshing ? "animate-spin" : "transition-transform group-hover:rotate-45"}`} />
            Rafraîchir
          </Button>
        }
      >
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-3">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Rechercher</Label>
              <div className="relative mt-2">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F]" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  placeholder="Patient, email, questionnaire…"
                  className="pl-9 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                  data-testid="assignments-search"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Questionnaire</Label>
              <Select value={filters.template_id} onValueChange={(v) => setFilters({ ...filters, template_id: v })}>
                <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]" data-testid="filter-template">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Statut</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="scheduled" data-testid="filter-scheduled">Programmés</SelectItem>
                  <SelectItem value="sent">En attente</SelectItem>
                  <SelectItem value="in_progress" data-testid="filter-in-progress">
                    En cours de réponse{inProgressCount > 0 ? ` (${inProgressCount})` : ""}
                  </SelectItem>
                  <SelectItem value="completed">Complété</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Du</Label>
              <Input
                type="date"
                lang="fr"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="mt-2 w-full bg-white border-[#E1E9F0]"
                data-testid="filter-date-from"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Au</Label>
              <Input
                type="date"
                lang="fr"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="mt-2 w-full bg-white border-[#E1E9F0]"
                data-testid="filter-date-to"
              />
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-[#5F758F]">{filtered.length} envoi{filtered.length > 1 ? "s" : ""}</p>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-filters">
                <X size={14} className="mr-1.5" /> Réinitialiser
              </Button>
            </div>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center">
            <p className="text-[#5F758F]">{items.length === 0 ? "Aucun envoi pour le moment." : "Aucun envoi ne correspond aux filtres."}</p>
          </div>
        ) : (
          <>
            {eligibleIds.length > 0 && (
              <div className={`px-5 py-3.5 rounded-[14px] border flex items-center gap-3 mb-3 transition-colors ${selectedInView.length > 0 ? "bg-[#1E3A5F] border-[#1E3A5F]" : "bg-[#EEF3F8] border-[#E1E9F0]"}`}>
                <Checkbox
                  checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAllInView}
                  aria-label="Tout sélectionner"
                  className={`h-5 w-5 rounded ${selectedInView.length > 0 ? "border-white/60 bg-white data-[state=checked]:bg-white data-[state=checked]:text-[#3A8FA5] data-[state=indeterminate]:bg-white data-[state=indeterminate]:text-[#3A8FA5]" : "border-[#3A8FA5] data-[state=checked]:bg-white data-[state=checked]:text-[#3A8FA5]"}`}
                  data-testid="asg-select-all"
                />
                <span className={`text-[13px] tracking-[0.05em] font-medium ${selectedInView.length > 0 ? "text-white" : "text-[#5F758F]"}`}>
                  {selectedInView.length > 0
                    ? `${selectedInView.length} sur ${eligibleIds.length} en attente sélectionné${selectedInView.length > 1 ? "s" : ""}`
                    : `${eligibleIds.length} envoi${eligibleIds.length > 1 ? "s" : ""} en attente sélectionnable${eligibleIds.length > 1 ? "s" : ""}`}
                </span>
                <span className={`ml-auto text-[13px] ${selectedInView.length > 0 ? "text-white/70" : "text-[#5F758F]"}`}>Actions groupées</span>
              </div>
            )}
            <div className="flex flex-col gap-2.5">
              {filtered.map((a) => {
                const isEligible = a.status === "sent" && !!a.patient_email && !a.template_is_deleted;
                const isChecked = selected.has(a.assignment_id);
                const draftCount = typeof a.draft_answered_count === "number"
                  ? a.draft_answered_count
                  : (a.draft_answers ? Object.keys(a.draft_answers).length : 0);
                const isInProgress = a.status === "sent" && !!a.draft_saved_at && draftCount > 0;
                const qTotal = a.template_question_count || 0;
                const progressPct = qTotal > 0 ? Math.min(100, Math.round((draftCount / qTotal) * 100)) : 0;
                return (
                  <div
                    key={a.assignment_id}
                    className={`relative bg-white border rounded-[14px] px-6 py-5 grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center transition-all overflow-hidden ${isChecked ? "border-[#3A8FA5] shadow-[0_2px_8px_rgba(58,143,165,0.12)]" : "border-[#E1E9F0] hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.08)]"}`}
                    data-testid={`asg-${a.assignment_id}`}
                  >
                    {isChecked && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#3A8FA5]" aria-hidden="true" />}
                    <div className="w-5 shrink-0 flex justify-center">
                      {isEligible ? (
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleOne(a.assignment_id)}
                          aria-label={`Sélectionner ${a.patient_name || "envoi"}`}
                          className="h-[18px] w-[18px] rounded border-[#3A8FA5] bg-white data-[state=checked]:bg-[#3A8FA5] data-[state=checked]:text-white"
                          data-testid={`select-${a.assignment_id}`}
                        />
                      ) : (
                        <span className="w-4 h-4 opacity-0" aria-hidden="true" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[16px] font-semibold text-[#1E3A5F] truncate">{a.patient_name || "Patient anonyme"}</p>
                      <p className="text-[15px] text-[#1E3A5F] truncate mt-0.5">{a.template_title}</p>
                      <p className="text-[13px] text-[#5F758F] mt-1">
                        {a.status === "scheduled" && a.scheduled_for ? (
                          <>Programmé pour le {new Date(a.scheduled_for).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                        ) : a.sent_at ? (
                          <>Envoyé le {new Date(a.sent_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                        ) : null}
                        {a.last_resend_at && a.status !== "completed" && a.status !== "scheduled" && (
                          <> · {a.resend_count || 1} relance{(a.resend_count || 1) > 1 ? "s" : ""}, dernière le {new Date(a.last_resend_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                        )}
                        {a.status === "completed" && a.completed_at && (
                          <> · Complété le {new Date(a.completed_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                        )}
                      </p>
                      {isInProgress && (
                        <div className="mt-2 flex items-center gap-2.5" data-testid={`draft-progress-${a.assignment_id}`}>
                          <div className="h-1.5 flex-1 max-w-[240px] rounded-full bg-[#E1E9F0] overflow-hidden">
                            <div className="h-full bg-[#3A8FA5] transition-all" style={{ width: `${progressPct}%` }} />
                          </div>
                          <span className="text-[12px] text-[#3A8FA5] font-semibold tabular-nums">
                            {draftCount}{qTotal > 0 ? ` / ${qTotal}` : ""} répondue{draftCount > 1 ? "s" : ""}
                            {a.draft_saved_at && (
                              <> · dernière activité {new Date(a.draft_saved_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>
                            )}
                          </span>
                        </div>
                      )}
                      {a.template_is_deleted && a.status !== "completed" && (
                        <p className="text-[13px] text-[#D45A3F] font-medium mt-1" data-testid={`tpl-deleted-${a.assignment_id}`}>
                          Questionnaire supprimé — lien invalide
                        </p>
                      )}
                    </div>
                    {a.status === "completed" ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#3A8FA5] text-[#3A8FA5]">Complété</span>
                    ) : a.status === "scheduled" ? (
                      <span className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#7A4EA8] text-[#7A4EA8] bg-[#F2E7F5]" data-testid={`badge-scheduled-${a.assignment_id}`}>
                        <Calendar size={12} weight="fill" /> Programmé
                      </span>
                    ) : isInProgress ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#3A8FA5] text-[#3A8FA5] bg-[#E6F1F5]" data-testid={`badge-in-progress-${a.assignment_id}`}>
                        En cours de réponse
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#D45A3F] text-[#D45A3F]">En attente</span>
                    )}
                    <div className="flex items-center gap-2 shrink-0">
                      {a.status === "sent" && !a.template_is_deleted && (
                        <Button
                          size="sm"
                          className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold px-4 text-[14px] h-10 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`resend-${a.assignment_id}`}
                          {...gateProps({
                            onClick: () => resend(a),
                            disabled: resendingId === a.assignment_id,
                            title: a.last_resend_at ? `Dernier renvoi le ${new Date(a.last_resend_at).toLocaleString("fr-FR")}` : "Renvoyer l'email au patient",
                          })}
                        >
                          <PaperPlaneTilt size={16} className="mr-1.5" />
                          {resendingId === a.assignment_id ? "Envoi…" : "Renvoyer"}
                        </Button>
                      )}
                      {a.status === "sent" && a.template_is_deleted && (
                        <span className="text-[12px] text-[#5F758F] italic" data-testid={`tpl-deleted-note-${a.assignment_id}`}>Renvoi impossible</span>
                      )}
                      {a.response_id && (
                        <Link to={`/responses/${a.response_id}`}>
                          <Button size="sm" className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold px-4 text-[14px] h-10">Voir la réponse</Button>
                        </Link>
                      )}
                      {(a.status === "sent" || a.status === "scheduled") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC] px-3 h-10 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`delete-${a.assignment_id}`}
                          {...gateProps({
                            onClick: () => setPendingDelete(a),
                            title: a.status === "scheduled" ? "Annuler cet envoi programmé" : "Supprimer cet envoi",
                          })}
                        >
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {selectedInView.length > 0 && (
          <div className="sticky bottom-4 mt-6 z-40 flex justify-center pointer-events-none">
            <div className="pointer-events-auto bg-[#1E3A5F] text-white shadow-2xl rounded-full pl-6 pr-2 py-2 flex items-center gap-5 border border-[#0F2340]" data-testid="bulk-action-bar">
              <p className="text-sm">
                <strong className="font-serif text-lg mr-1.5">{selectedInView.length}</strong>
                envoi{selectedInView.length > 1 ? "s" : ""} sélectionné{selectedInView.length > 1 ? "s" : ""}
              </p>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs uppercase tracking-[0.18em] text-white/60 hover:text-white transition-colors"
                data-testid="bulk-clear"
              >Annuler</button>
              <Button
                onClick={bulkResend}
                disabled={bulkBusy}
                className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white h-11 px-6 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-resend"
                {...gateProps({
                  onClick: bulkResend,
                  disabled: bulkBusy,
                  title: `Renvoyer ${selectedInView.length} rappel${selectedInView.length > 1 ? "s" : ""}`,
                })}
              >
                <PaperPlaneTilt size={16} weight="duotone" className="mr-2" />
                {bulkBusy ? "Envoi…" : `Renvoyer ${selectedInView.length} rappel${selectedInView.length > 1 ? "s" : ""}`}
              </Button>
            </div>
          </div>
        )}

        <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v && !deletingAsg) setPendingDelete(null); }}>
          <AlertDialogContent className="bg-white" data-testid="delete-asg-dialog">
            <AlertDialogHeader>
              <div className={`mx-auto sm:mx-0 mb-2 w-12 h-12 rounded-[14px] flex items-center justify-center border ${pendingDelete?.status === "scheduled" ? "bg-[#F2E7F5] border-[#D4B8DE]" : "bg-[#FFE0DC] border-[#EEC1BB]"}`}>
                <WarningCircle size={26} weight="duotone" className={pendingDelete?.status === "scheduled" ? "text-[#7A4EA8]" : "text-[#8B2A1E]"} />
              </div>
              <AlertDialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">
                {pendingDelete?.status === "scheduled" ? "Annuler cet envoi programmé ?" : "Supprimer cet envoi ?"}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#5F758F] leading-relaxed">
                {pendingDelete?.status === "scheduled" ? (
                  <>
                    L'envoi <strong className="text-[#1E3A5F]">« {pendingDelete?.template_title} »</strong> à <strong className="text-[#1E3A5F]">{pendingDelete?.patient_name || "ce patient"}</strong> était programmé{pendingDelete?.scheduled_for ? <> pour le <strong className="text-[#1E3A5F]">{new Date(pendingDelete.scheduled_for).toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</strong></> : ""}. Il n'a pas encore été envoyé.
                    <span className="block mt-3 text-xs">
                      En annulant, l'email ne sera pas envoyé et aucune trace ne sera conservée dans la fiche du patient.
                    </span>
                  </>
                ) : (
                  <>
                    Vous êtes sur le point de supprimer l'envoi <strong className="text-[#1E3A5F]">« {pendingDelete?.template_title} »</strong> à <strong className="text-[#1E3A5F]">{pendingDelete?.patient_name || "ce patient"}</strong>. Le lien envoyé au patient deviendra invalide.
                    <span className="block mt-3 text-xs">
                      {pendingDelete?.draft_saved_at
                        ? "Attention : ce patient a commencé à répondre — son brouillon sera perdu. "
                        : ""}
                      Les réponses <strong>déjà complétées et enregistrées</strong> (section Réponses) ne sont jamais affectées par cette action.
                    </span>
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-[10px]" disabled={deletingAsg}>Retour</AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  if (!pendingDelete) return;
                  const wasScheduled = pendingDelete.status === "scheduled";
                  setDeletingAsg(true);
                  try {
                    await api.delete(`/assignments/${pendingDelete.assignment_id}`);
                    toast.success(wasScheduled ? "Envoi programmé annulé" : "Envoi supprimé");
                    setPendingDelete(null);
                    load();
                    setSelected((prev) => { const next = new Set(prev); next.delete(pendingDelete.assignment_id); return next; });
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || (wasScheduled ? "Annulation impossible" : "Suppression impossible"));
                  } finally { setDeletingAsg(false); }
                }}
                disabled={deletingAsg}
                className={`rounded-[10px] text-white font-semibold ${pendingDelete?.status === "scheduled" ? "bg-[#7A4EA8] hover:bg-[#5E3B84]" : "bg-[#8B2A1E] hover:bg-[#6D2117]"}`}
                data-testid="delete-asg-confirm"
              >
                {deletingAsg
                  ? (pendingDelete?.status === "scheduled" ? "Annulation…" : "Suppression…")
                  : (pendingDelete?.status === "scheduled" ? "Annuler l'envoi" : "Supprimer l'envoi")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppShell>
    </Protected>
  );
}
